import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { getFileUrl } from '@utils/getFileUrl'

import { useModal } from '@hooks/app/modal.hook'

import { Input } from '@components/Input'
import { Modal } from '@components/Modal'
import { Refresh } from '@components/Refresh'
import { UploadPicture } from '@components/UploadPicture'

import { useProjectWithSubscription } from '@hooks/project/project-with-subscription.hook'
import { useUpdateProject } from '@hooks/project/update-project.hook'

import { LocalFile, ProjectContact, RemoteFile } from '@types'

import { ContactList } from '../../components/ContactList'

import { Container, Section, Title } from './edit.styled'
import { v4 } from 'uuid'

export const EditPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const { projectId } = useParams()

  const { data, loading, error, refetch } = useProjectWithSubscription(projectId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const project = data?.project

  const [projectName, setProjectName] = useState<string>('')
  const [companyName, setCompanyName] = useState<string>('')

  const [photos, setPhotos] = useState<(LocalFile | RemoteFile)[]>([])

  const { updateProject } = useUpdateProject()

  const loadPhotoPreview = async () => {
    if (project && project.photos[0]?.url) {
      const photo = project.photos[0]
      const url = await getFileUrl(photo.url)

      const remoteFile: RemoteFile = {
        id: photo.id,
        deleted: false,
        name: photo.file.name,
        type: photo.file.type,
        extension: photo.file.extension,
        url,
      }
      setPhotos([remoteFile])
    }
  }

  const [contacts, setContacts] = useState<ProjectContact[]>([])

  const onAddContact = () => {
    const contact: ProjectContact = {
      id: v4(),
      role: '',
      companyName: '',
      order: contacts.length,
    }

    setContacts([...contacts, contact])
  }

  const onRemoveContact = (id: string) => {
    const newContacts = contacts.filter((contact) => contact.id !== id)

    setContacts(newContacts)
  }

  const onUpdateContact = (id: string, data: Partial<ProjectContact>) => {
    const newContacts = contacts.map((contact) => {
      if (contact.id === id) {
        return {
          ...contact,
          ...data,
        }
      }
      return contact
    })

    setContacts(newContacts)
  }

  const onMoveContactUp = (id: string) => {
    const contactIndex = contacts.findIndex((c) => c.id === id)
    if (contactIndex <= 0) return

    const newContacts = [...contacts]
    const temp = newContacts[contactIndex]

    newContacts[contactIndex] = newContacts[contactIndex - 1]
    newContacts[contactIndex - 1] = temp

    newContacts.forEach((contact, index) => {
      contact.order = index
    })

    setContacts(newContacts)
  }

  const onMoveContactDown = (id: string) => {
    const contactIndex = contacts.findIndex((c) => c.id === id)
    if (contactIndex === -1 || contactIndex >= contacts.length - 1) return

    const newContacts = [...contacts]
    const temp = newContacts[contactIndex]

    newContacts[contactIndex] = newContacts[contactIndex + 1]
    newContacts[contactIndex + 1] = temp

    newContacts.forEach((contact, index) => {
      contact.order = index
    })

    setContacts(newContacts)
  }

  useEffect(() => {
    if (project) {
      setProjectName(project.name)
      setCompanyName(project.companyName)

      if (project.contacts) {
        const contacts = project.contacts.map((contact) => ({
          id: contact.id,
          role: contact.role,
          fullName: contact.fullName,
          address: contact.address,
          phone: contact.phone,
          email: contact.email,
          city: contact.city,
          zipCode: contact.zipCode,
          companyName: contact.companyName,
          order: contact.order,
        }))

        setContacts(contacts)
      }

      loadPhotoPreview()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  return (
    <Modal
      title={t('modules_project_pages_edit_title')}
      onSubmitLabel={t('modules_project_pages_edit_button_edit-project')}
      onClose={() => {
        hideModal('update_project')
      }}
      onSubmit={() => {
        if (project)
          updateProject(
            {
              id: project.id,
              name: projectName,
              companyName,
              contacts: contacts,
            },
            photos,
            [],
          )

        hideModal('update_project')
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(project?.id || '')}>
          {project && (
            <>
              <Section>
                <Title>{t('modules_project_pages_edit_project_label_section_project')}</Title>
                <Input
                  autoFocus
                  required
                  label={t('modules_project_pages_edit_project_label_project-name')}
                  placeholder={t('modules_project_pages_edit_project_placeholder_project-name')}
                  value={projectName}
                  onChange={(v) => setProjectName(v)}
                />
                <Input
                  required
                  label={t('modules_project_pages_edit_project_label_company_name')}
                  placeholder={t('modules_project_pages_edit_project_placeholder_company_name')}
                  value={companyName}
                  onChange={(v) => setCompanyName(v)}
                />

                <UploadPicture
                  label={t('modules_project_pages_edit_project_label_project_photo')}
                  files={photos}
                  setFiles={(files) => setPhotos(() => [...files])}
                />
              </Section>

              <Section>
                <Title>{t('modules_project_pages_edit_project_label_section_actors')}</Title>
                <ContactList
                  contacts={contacts}
                  onAdd={onAddContact}
                  onRemove={onRemoveContact}
                  onUpdate={onUpdateContact}
                  onMoveUp={onMoveContactUp}
                  onMoveDown={onMoveContactDown}
                />
              </Section>
            </>
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
